<template>
  <div>
    <b-container class="container-panel">
      <b-row align-v="end">
        <b-col cols="4">
          <img src="./assets/images/logoSmall.jpg"
               class="logo-margin"
               alt="Ambit Cares Logo"
               aria-label="Ambit Cares Logo" />
        </b-col>
        <b-col cols="8">
          <span class="page-title align-center">Verifying...</span>
        </b-col>
      </b-row>
    </b-container>

    <b-container>
      <b-row>
        <!--<b-col cols="5"></b-col>-->
        <b-col cols="12"
               class="d-flex justify-content-center mb-3 make-space-above ambit-orange">
          <b-spinner></b-spinner>
        </b-col>
      </b-row>
    </b-container>
  </div>
</template>

<script>

  import Vue from 'vue'
  import Vuex from 'vuex'
  import axios from 'axios';
  import VueAxios from 'vue-axios';
  import constants from '../constants';
  import utilities from '../utilities';

  Vue.use(Vuex);
  Vue.use(VueAxios, axios);

  export default {
    name: 'DonationWithCardScan',
    data() {
      return {
        donorType: '',
        donorNumber: '',
        donorName: '',
        amount: 0.0,
        zipCode: '',
        locationCode: '',
        callBackUrl: 'ambitcares.org',
      };
    },

    // -----------------------------------------------------------------------
    // The created method is part of the Vue.js lifecycle.
    // It gets called as the page is created.
    // This sanity checks the information received as part of the URL
    // and uses it to set a request for additional information to the
    // server
    // -----------------------------------------------------------------------
    created() {
      // clean out any existing error messages and the transfered data
      this.$store.commit('clearErrorMessages');
      this.$store.commit('clearInitialData');

      // set up the initial data (from the parsing of the route)
      this.donorType = utilities.capitalizeFirstLetter(this.$route.params.accountType);

      // two exclamation marks (!!) does a test for truthiness (it is two NOTs (!))
      // this is a consultant or customer if there is an account number coming in
      const isConsultantOrCustomer = !!this.$route.params.accountNumber;

      if (this.$route.params.accountNumber) {
        this.donorNumber = this.$route.params.accountNumber;
      }

      if (this.$route.params.donorName) {
        this.donorName = decodeURI(this.$route.params.donorName);
      }

      if (this.$route.params.zipCode) {
        this.zipCode = utilities.stripZipPlusFour(this.$route.params.zipCode);
      }

      this.amount = this.$route.params.amount;
      this.locationCode = this.$route.query.locationCode;
      // check the validity of that data
      if (!this.donorType) {
        this.$store.commit('addErrorMessage', 'Missing Donor Type');
      } else if (['Consultant', 'Employee', 'Customer', 'Friend'].indexOf(this.donorType) < 0) {
        this.$store.commit('addErrorMessage', `Incorrect Donor Type: [${this.donorType}]`);
      }

      if (isConsultantOrCustomer) {
        if (!this.donorNumber) {
          this.$store.commit('addErrorMessage', 'Missing Donor Number');
        } else if (!this.donorNumber.match(constants.cNumberRegex)) {
          this.$store.commit('addErrorMessage', `Incorrectly formatted Donor Number: [${this.donorNumber}]`)
        }

        // Only customers and consultants get zip codes in their URLs
        if (!this.zipCode) {
          this.$store.commit('addErrorMessage', 'Missing Zip Code');
        } else if (this.zipCode.length !== 5 || Number.isNaN(Number(this.zipCode))) {
          this.$store.commit('addErrorMessage', `Incorrectly formatted Zip Code: [${this.zipCode}]`)
        }
      }

      if (!isConsultantOrCustomer) {
        if (!this.donorName) {
          this.$store.commit('addErrorMessage', 'Missing Donor Name');
        }
      }

      if (!this.amount) {
        this.$store.commit('addErrorMessage', 'Missing Amount');
      } else if (Number.isNaN(Number(this.amount))) {
        this.$store.commit('addErrorMessage', `Incorrectly formatted Donation Amount: [${this.amount}]`)
      }


      if (this.$store.getters.anyErrors) {
        this.$router.push({ name: 'ErrorReport' });
        return;
      }

      // if the data is good, then put it in the store
      this.$store.commit('setInitialData', {
        donorType: this.donorType,
        donorNumber: this.donorNumber,
        donorName: this.donorName,
        amount: this.amount,
        zipCode: this.zipCode,
        locationCode: this.locationCode,
      });

      // we are all set to go, so go to the server to verify that everything is good (for Consultants and Customers)
      if (isConsultantOrCustomer) {
        this.validateDonor();
      } else {
        // we don't need to go to the server for friends or employees
        const request = {
          ExternalKeyType: this.donorType,
          AccountHolderName: this.donorName,
          Amount: this.amount,
          LocationId: this.locationCode,
          CallBackUrl: this.callBackUrl,
        }
        this.getPaymentToken(request);
      }
      // Nothing to do here - validateDonor is asynchronous
    },

    methods: {

      // -----------------------------------------------------------------------
      // This is used to call back to the server and get the data for the particular donorType/donorNumber/zipCode
      // combination.  If successful, it sorts the "additional" information and goes to the "NormalDonation page"
      // -----------------------------------------------------------------------
      validateDonor() {
        const infoMessage = 'To verify your account information, please contact customer care at 877-282-6284.';
        const zipCodeNote = '(provided zipcode does not match account information).';
        const url = '/api/donordonation/IsDonorValid';
        // store these locally so that they don't get lost while processing promises
        const stateStore = this.$store;
        const router = this.$router;
        const self = this;
        // go up to the server to get the rest of the data
        this.axios.post(url, {
          donorType: this.donorType,
          donorNumber: this.donorNumber,
          zipCode: self.zipCode,
          })
          .then((result) => {
            if (!result.data.Error) {
              const resultData = result.data.Data;
              if (resultData) {
                // This is the HAPPY PATH
                if (resultData.Result) {
                  const request = {
                    Amount: self.amount,
                    ExternalKeyType: this.donorType,
                    ExternalKeyValue: this.donorNumber.toUpperCase(),
                    CallBackUrl: this.callBackUrl,
                  }
                  this.getPaymentToken(request);
                  return;
                }
                stateStore.commit('addErrorMessage', `Zipcode mis-match for ${self.donorType} ${zipCodeNote} ${infoMessage}`);
                router.push({ name: 'ErrorReport' });
                return;
              }
            }
            // if I get here, something went wrong, so...
            stateStore.commit('addErrorMessage', `Error accessing ${self.donorType} account. ${infoMessage}`);
            router.push({ name: 'ErrorReport' });
          })
          .catch((ex) => {
            stateStore.commit('addErrorMessage', `Exception accessing ${self.donorType} account. (ex: ${ex.message})`);
            router.push({ name: 'ErrorReport' });
          });
      },
      getPaymentToken(payload) {
        axios.post('api/payment/GetPaymentToken', payload)
          .then((response) => {
            const token = response.data.D.Data.Result.PaymentToken;
            this.$store.commit('setPaymentToken', response.data.D.Data.Result.PaymentToken);
            this.redirectToPaymentUi(token);
          })
      },
      redirectToPaymentUi(token) {
        const url = window.location.href;
        if (url.includes('localhost') || url.includes('int')) {
          window.location.assign(`https://payment.int.ambitenergy.io/#/paymentui/${token}`);
        } else {
          window.location.assign(`https://payment.ambitenergy.com/#/paymentui/${token}`);
        }
      },

    },
  }
</script>

<style src="./assets/css/CommonVue.css" scoped>
</style>

<style scoped>
</style>
